import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";

/**
 * イベント API 操作クラス
 */
export default class EventReplies {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * GET EventReplies
   */
  public async get(userStore: LoginUser, eventRepliesSortKey: string): Promise<EventRepliesGetModel> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      eventRepliesSortKey: eventRepliesSortKey
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.EventReplies, formatParams);
    const response = await this.commonService.executeGet<EventRepliesGetModel>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * GET EventReplies
   */
  public async gets(userStore: LoginUser, eventId: number): Promise<Array<EventRepliesGetModel>> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      eventId: eventId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.EventRepliesInEvent, formatParams);
    const response = await this.commonService.executeGet<Array<EventRepliesGetModel>>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * POST EventReplies
   */
  public async post(userStore: LoginUser, requestBody: EventRepliesPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.EventReply, formatParams);

    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * PATCH EventReplies
   */
  public async patch(userStore: LoginUser, eventRepliesSortKey: string, requestBody: EventRepliesPathModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      eventRepliesSortKey: eventRepliesSortKey
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.EventReplies, formatParams);

    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * DELETE EventReplies
   */
  public async delete(userStore: LoginUser, eventRepliesSortKey: string): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      eventRepliesSortKey: eventRepliesSortKey
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.EventReplies, formatParams);
    const response = await this.commonService.executeDelete<any>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

export interface EventRepliesGetModel {
  /** 施設ID */
  facilityId: number;
  /** ソートキー参照 */
  eventRepliesSortKey: string;
  /** イベントのソートキー */
  parentEventsSortKey: number;
  /** 利用者ID */
  residentId: number;
  /** 利用者名 */
  residentName: string;
  /** 利用者部屋名 */
  residentRoom: string;
  /** 家族ID */
  familyId: number;
  /** 家族のLINE ID */
  familyLineId: string;
  /** 家族名 */
  familyName: string;
  /** 家族続柄 */
  familyRelation: string;
  /** 参加者情報 */
  attendees: Array<Attendees>;
  /** 削除フラグ */
  deleteFlag: boolean;
  /** 登録日時 */
  created: number;
  /** 更新日時 */
  updated: number;
}

export interface Attendees {
  name: string;
  attendanceType: AttendType;
}

export enum AttendType {
  LOCAL = 0,
  ONLINE = 1
}

export interface EventRepliesPostModel {
  /** ソートキー。(parentEventsSortKey)_(residentId)_(familyId) */
  parentEventsSortKey: number;
  /** 利用者ID */
  residentId: number;
  /** 家族ID */
  familyId: number;
  /** 家族のLINE ID */
  familyLineId: string;
  /** 参加者情報 */
  attendees: Array<Attendees>;
}

export interface EventRepliesPathModel {
  /** 参加者情報 */
  attendees: Array<Attendees>;
  /** 更新日時 */
  updated: number;
}
